import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { EditOutlined } from '@ant-design/icons';
import { Button, Card, Col, Row } from 'antd';
import { isEmpty } from 'lodash';
import { TableSecondaryText } from 'styles';

import { TableTimestamp } from 'components';
import AuditLog from 'components/AuditLog';
import RenderInGrid from 'components/Layout/RenderInGrid';
import { GridContainer, Tag } from 'components/Layout/common';
import Label from 'components/Text/Label';
import { ActionsBtnGroup, SectionHeader } from 'containers/BDO/utils';
import { IParamsWithContainId } from 'utils/types';

import BDOSettingAddEditModal from '../BDOSettingAddEditModal';
import { getBDOSetting } from '../action';

const CashCreditShow = () => {
  const params = useParams<IParamsWithContainId>();
  const cashCreditId = params.id;

  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const { bdoSetting } = useSelector((state: any) => state.bdoSettings);

  useEffect(() => {
    if (cashCreditId) {
      dispatch(getBDOSetting(cashCreditId));
    }
  }, [cashCreditId, dispatch]);

  const handleEditClick = () => {
    setIsEdit(true);
    setIsModalOpen(true);
  };

  const handleDuplicateClick = () => {
    setIsEdit(false);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const renderInGrid = (description, content, hasContentChanged?: boolean) => {
    return (
      <RenderInGrid
        description={description}
        content={content}
        hasContentChanged={hasContentChanged}
      />
    );
  };

  const renderTags = (tags = []) => {
    if (isEmpty(tags)) {
      if (!tags.length) {
        return <Label>N.A</Label>;
      }
    }
    if (!isEmpty(tags)) {
      return tags.map(tag => {
        return tag?.id ? (
          <Tag key={tag.id}>
            <Label nomargin>{tag.name}</Label>
          </Tag>
        ) : null;
      });
    } else {
      return <Label>N.A</Label>;
    }
  };

  return (
    <>
      <Card>
        <Row justify="space-between">
          <SectionHeader>Diners Cash Credit Details</SectionHeader>
          <ActionsBtnGroup>
            <Button size="large" ghost type="primary" onClick={handleDuplicateClick}>
              Duplicate
            </Button>
            <Button size="large" type="primary" icon={<EditOutlined />} onClick={handleEditClick}>
              Edit
            </Button>
          </ActionsBtnGroup>
        </Row>
        {renderInGrid('Name:', <Label dark>{bdoSetting?.name}</Label>)}
        {renderInGrid('Description:', <Label dark>{bdoSetting?.description}</Label>)}

        {renderInGrid(
          'Conversion Rate:',
          <Label dark>
            {bdoSetting?.conversion_rate} Credit Card Points = 1 {bdoSetting?.currency}
          </Label>
        )}
        {renderInGrid(
          'Min. Redeem Amt.:',
          <Label dark>{Math.floor(bdoSetting?.min_redeem)} points</Label>
        )}
        {renderInGrid(
          'Pts Redeem Increment:',
          <Label dark>{Math.floor(bdoSetting?.increment_amount)} points</Label>
        )}
        {renderInGrid('LRS Reward Code:', <Label dark>{bdoSetting?.lrs_reward_code}</Label>)}
        {renderInGrid('Applicable Credit Cards:', renderTags(bdoSetting?.cards))}
        {renderInGrid(
          'Start & End Date:',
          <Label dark>
            <Col>
              <GridContainer columns="auto 10px auto" gap="10px" inline>
                {bdoSetting?.start_date ? (
                  <TableTimestamp timestamp={bdoSetting.start_date} />
                ) : (
                  'N/A'
                )}
                <TableSecondaryText>~</TableSecondaryText>
                {bdoSetting?.end_date ? <TableTimestamp timestamp={bdoSetting.end_date} /> : 'N/A'}
              </GridContainer>
            </Col>
          </Label>
        )}
      </Card>
      <AuditLog
        entity="bdo_transfer_request_card_item"
        id={cashCreditId}
        isChanges2
        key={bdoSetting?.id}
      />
      <BDOSettingAddEditModal
        modalOpen={isModalOpen}
        closeModal={handleCloseModal}
        isEdit={isEdit}
        selectedBDOSetting={bdoSetting?.id}
        title={'Diners Cash Credit Details'}
        BDOSettingType={'CashCredit'}
        cardItemType={'cash_credit'}
      />
    </>
  );
};

export default CashCreditShow;
